import React from 'react';
import { useField } from 'react-final-form';
import {
  FormControl, FormErrorMessage, Input,
} from '@chakra-ui/react';
import FieldLabel from './FieldLabel';

interface DateInputControlProps {
    name: string;
    label: string;
    isRequired?: boolean;
}
const DateInputControl: React.FC<DateInputControlProps> = ({
  name, label, isRequired, ...rest
}) => {
  // item 514 — <input type="date"> ONLY accepts yyyy-MM-dd. After an auto-save round-trip the
  // form value can come back as an ISO datetime (2026-07-07T00:00:00), which the browser SILENTLY
  // CLEARS — the field looked like it "wiped itself" and blocked saving (Chad). Always feed the
  // input the yyyy-MM-dd slice; the picker hands back yyyy-MM-dd, so store it as-is.
  const { input, meta } = useField<string>(name, {
    format: (v) => (typeof v === 'string' ? v.slice(0, 10) : ''),
    parse: (v) => v,
  });
  const invalid = Boolean(meta.error) && meta.touched;
  // item 515 → 1024 — the calendar-picker icon's dark-mode visibility is handled GLOBALLY by
  // the item-877 color-scheme rule (brandTheme): the browser draws a light icon on a dark page.
  // The old invert(1) filter here DOUBLE-APPLIED with it — color-scheme turned the icon white,
  // the filter flipped it back to black (Chad's "same icon black issue"). No filter, ever.
  return (
    <FormControl {...rest} isInvalid={invalid} isRequired={isRequired}>
      <FieldLabel htmlFor={name}>{label}</FieldLabel>
      <Input
        type="date"
        {...input}
        isInvalid={invalid}
        id={name}
        placeholder={label}
        sx={{ '::-webkit-calendar-picker-indicator': { cursor: 'pointer', opacity: 0.85 } }}
        // item 291 — clicking ANYWHERE in the box opens the picker, not just the tiny
        // calendar icon (showPicker needs a user gesture; guarded for older browsers).
        onClick={(e) => {
          const el = e.currentTarget as HTMLInputElement & { showPicker?: () => void };
          try { el.showPicker?.(); } catch { /* unsupported / no activation — ignore */ }
        }}
      />
      <FormErrorMessage>{meta.error}</FormErrorMessage>
    </FormControl>
  );
};
DateInputControl.defaultProps = {
  isRequired: false,
};
export default DateInputControl;
